import { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation, LOCALES } from "../i18n/i18n";
import StickyTitle from "../components/StickyTitle/StickyTitle";
import Loader from "../components/Loader/Loader";
import Modal from "../components/Modal/Modal";
import ReauthForm from "../components/AuthForm/ReauthForm";
import { auth, db } from "../firebase";
import {
	sendPasswordResetEmail,
	signOut,
	deleteUser,
	onAuthStateChanged,
	sendEmailVerification,
	EmailAuthProvider,
	reauthenticateWithCredential,
	verifyBeforeUpdateEmail,
} from "firebase/auth";
import { doc, getDoc, deleteDoc, setDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
	LogOut,
	MailCheck,
	Trash2,
	Pencil,
	ShieldUser,
	Languages,
	SunMoon,
	CircleUser,
	Paintbrush,
	FolderDown,
	AlertTriangle,
	X,
	Check,
} from "lucide-react";
import { UserPreferencesContext } from "../contexts/UserPreferencesContext";
import { toast } from "react-toastify";

const Settings = () => {
	const { translate } = useTranslation();
	const {
		themePreference,
		setThemePreference,
		locale,
		setLocale,
		authInitialized,
		systemTheme,
		design,
		setDesign,
	} = useContext(UserPreferencesContext);
	const [user, setUser] = useState(null);
	const [emailVerified, setEmailVerified] = useState(null);
	const [selectedLanguage, setSelectedLanguage] = useState(locale);
	const [isExporting, setIsExporting] = useState(false);
	const { bottomNavStyle, setBottomNavStyle } = useContext(
		UserPreferencesContext
	);
	const [buildId, setBuildId] = useState("Unknown");
	const navigate = useNavigate();
	const [userData, setUserData] = useState(null);

	const handleNavigateToAdmin = useCallback(() => {
		navigate("/admin");
	}, [navigate]);

	// States for reauthentification modal
	const [showReauthModal, setShowReauthModal] = useState(false);
	const [pendingAction, setPendingAction] = useState(null);
	const [reauthPassword, setReauthPassword] = useState("");
	const [newEmail, setNewEmail] = useState("");
	const [isReauthLoading, setIsReauthLoading] = useState(false);

	// States for confirmation modals
	const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
	const [showEmailChangeModal, setShowEmailChangeModal] = useState(false);
	const [emailInputValue, setEmailInputValue] = useState("");

	// Initialize Firebase Functions
	const functions = getFunctions(undefined, "europe-west1");

	useEffect(() => {
		fetch(`/buildId.json?ts=${Date.now()}`)
			.then(res => res.json())
			.then(data => setBuildId(data.id?.trim() || "Unknown"))
			.catch(() => setBuildId("Unknown"));
	}, []);

	useEffect(() => {
		setSelectedLanguage(locale);
	}, [locale]);

	useEffect(() => {
		const unsubscribe = onAuthStateChanged(auth, async user => {
			if (user) {
				setUser(user);
				setEmailVerified(user.emailVerified);
				const docRef = doc(db, "users", user.uid);
				const docSnap = await getDoc(docRef);
				if (docSnap.exists()) {
					setUserData(docSnap.data());
				}
			} else {
				setUser(null);
				setEmailVerified(false);
				setUserData(null);
			}
		});
		return () => unsubscribe();
	}, []);

	if (!authInitialized || emailVerified === null) {
		return (
			<div className="content">
				<StickyTitle title={translate("settings.title")} />
				<div className="page-content">
					<Loader
						fullscreen={true}
						loadingTextKey="settings.loading"
						theme="light"
						translate={translate}
					/>
				</div>
			</div>
		);
	}

	const handleResendVerification = async () => {
		const currentUser = auth.currentUser;

		console.log("Resending verification email:", {
			user: currentUser?.email,
			emailVerified: currentUser?.emailVerified,
		});

		if (!currentUser) {
			toast.error(translate("error.userNotLoggedIn"));
			return;
		}

		if (currentUser.emailVerified) {
			toast.info(translate("alert.emailAlreadyVerified"));
			return;
		}

		try {
			// Reload user data to ensure it's up to date
			await currentUser.reload();
			const refreshedUser = auth.currentUser;

			console.log("Sending verification email to:", refreshedUser?.email);
			await sendEmailVerification(refreshedUser);

			toast.success(translate("alert.verificationEmailSent"));
		} catch (error) {
			console.error("Error sending verification email:", error);

			let errorMessage = translate("alert.verificationEmailError");
			if (error.code === "auth/too-many-requests") {
				errorMessage = translate("alert.tooManyRequests");
			}

			toast.error(errorMessage);
		}
	};

	const handleSystemThemeToggle = e => {
		const useSystem = e.target.checked;
		setThemePreference(useSystem ? "system" : systemTheme);
	};

	const handleManualThemeToggle = e => {
		setThemePreference(e.target.checked ? "dark" : "light");
	};

	const handleLanguageChange = e => {
		const newLang = e.target.value;
		setSelectedLanguage(newLang);
		setLocale(newLang);
	};

	const handlePasswordReset = () => {
		if (!user?.email) return;
		sendPasswordResetEmail(auth, user.email)
			.then(() =>
				toast.success(
					translate("alert.resetPasswordSent", { email: user.email })
				)
			)
			.catch(() => toast.error(translate("alert.genericError")));
	};

	const handleLogout = () => {
		signOut(auth).then(() => (window.location.href = "/auth"));
	};

	const reauthenticateUser = async password => {
		const user = auth.currentUser;
		if (!user || !user.email) {
			throw new Error(translate("alert.userNotConnected"));
		}
		const credential = EmailAuthProvider.credential(user.email, password);
		await reauthenticateWithCredential(user, credential);
	};

	const handleExport = async () => {
		setIsExporting(true);
		const toastId = toast.loading(translate("settings.exportLoading"));

		try {
			const exportResult = await httpsCallable(functions, "exportUserData")();
			const blob = new Blob([JSON.stringify(exportResult.data, null, 2)], {
				type: "application/json",
			});
			const url = URL.createObjectURL(blob);

			const link = document.createElement("a");
			link.href = url;
			link.download = "numerys_export.json";
			link.click();
			URL.revokeObjectURL(url);

			toast.update(toastId, {
				render: translate("alert.exportSuccess"),
				type: "success",
				isLoading: false,
				autoClose: 3000,
			});
		} catch (error) {
			console.error("Export failed", error);
			toast.update(toastId, {
				render: translate("alert.exportFailed"),
				type: "error",
				isLoading: false,
				autoClose: 4000,
			});
		} finally {
			setIsExporting(false);
		}
	};

	const executeDeleteAccount = async (retryWithReauth = false) => {
		if (!user) return;

		const toastId = toast.loading(translate("settings.deletingAccount"));

		try {
			if (retryWithReauth && reauthPassword) {
				await reauthenticateUser(reauthPassword);
			}

			await deleteDoc(doc(db, "users", user.uid));
			await deleteUser(user);
			await signOut(auth);

			toast.update(toastId, {
				render: translate("settings.accountDeleted"),
				type: "success",
				isLoading: false,
				autoClose: 3000,
			});

			setPendingAction(null);
			setReauthPassword("");
			setShowReauthModal(false);
			setIsReauthLoading(false);

			setTimeout(() => {
				window.location.href = "/auth";
			}, 1000);
		} catch (error) {
			console.error("Error deleting user:", error);

			if (
				(error.code === "auth/requires-recent-login" ||
					error.code === "auth/wrong-password") &&
				!retryWithReauth
			) {
				toast.dismiss(toastId);
				setPendingAction("deleteAccount");
				setShowReauthModal(true);
				return;
			}

			let errorMessage = translate("alert.genericError");
			if (error.code === "auth/wrong-password") {
				errorMessage = translate("alert.wrongPassword");
			}

			toast.update(toastId, {
				render: errorMessage,
				type: "error",
				isLoading: false,
				autoClose: 4000,
			});
		}
	};

	const executeEmailChange = async (emailToSet, retryWithReauth = false) => {
		if (!user) {
			console.error("No user found");
			return;
		}

		const targetEmail = emailToSet || newEmail;

		console.log("Starting email change process:", {
			currentEmail: user.email,
			newEmail: targetEmail,
			userId: user.uid,
		});

		const toastId = toast.loading(translate("alert.updatingEmail"));

		try {
			// 1. Re-authentication if necessary
			if (retryWithReauth && reauthPassword) {
				console.log("Re-authenticating user...");
				await reauthenticateUser(reauthPassword);
				console.log("Re-authentication successful");
			}

			// 2. Check that Firestore document exists
			const userDocRef = doc(db, "users", user.uid);
			console.log("Checking existing Firestore document...");
			const userDocSnap = await getDoc(userDocRef);

			if (!userDocSnap.exists()) {
				console.error("User document does not exist in Firestore");
				throw new Error(translate("alert.userDocumentNotFound"));
			}

			const currentData = userDocSnap.data();
			console.log("Current Firestore data:", currentData);

			// 3. USE verifyBeforeUpdateEmail instead of updateEmail
			console.log("Sending verification email to new address...");
			await verifyBeforeUpdateEmail(user, targetEmail);
			console.log("Verification email sent successfully");

			// 4. Update Firestore with "pending" status
			console.log("Updating Firestore with pending email...");
			await setDoc(userDocRef, {
				...currentData,
				pendingEmail: targetEmail, // Email awaiting verification
				pendingEmailAt: new Date().toISOString(),
				// Don't change main email until it's verified
			});

			toast.update(toastId, {
				render: translate("alert.verificationEmailSent"),
				type: "success",
				isLoading: false,
				autoClose: 8000,
			});

			// 5. Clean up states
			setPendingAction(null);
			setReauthPassword("");
			setNewEmail("");
			setShowReauthModal(false);
			setIsReauthLoading(false);

			// 6. Inform user they need to verify their email
			setTimeout(() => {
				toast.info(
					translate("alert.verificationInstructionsForUpdate", {
						email: targetEmail,
					}),
					{ autoClose: 10000 }
				);
			}, 2000);
		} catch (error) {
			console.error("Error updating email:", error);

			// Specific handling for re-authentication errors
			if (
				(error.code === "auth/requires-recent-login" ||
					error.code === "auth/wrong-password") &&
				!retryWithReauth
			) {
				console.log("Re-authentication required");
				toast.dismiss(toastId);
				setPendingAction("changeEmail");
				setShowReauthModal(true);
				return;
			}

			// More specific error messages
			let errorMessage = translate("alert.genericError");
			switch (error.code) {
				case "auth/invalid-email":
					errorMessage = translate("alert.invalidEmail");
					break;
				case "auth/email-already-in-use":
					errorMessage = translate("alert.emailInUse");
					break;
				case "auth/wrong-password":
					errorMessage = translate("alert.wrongPassword");
					break;
				case "auth/requires-recent-login":
					errorMessage = translate("alert.reauthRequired");
					break;
				case "auth/operation-not-allowed":
					errorMessage = errorMessage = translate("alert.operationNotAllowed");
					break;
				default:
					errorMessage =
						translate("alert.emailUpdateError") + ": " + error.message;
			}

			toast.update(toastId, {
				render: errorMessage,
				type: "error",
				isLoading: false,
				autoClose: 6000,
			});
		}
	};

	// Handlers for confirmation modals
	const handleDeleteAccount = () => {
		if (!user) return;
		setShowDeleteConfirmModal(true);
	};

	const confirmDeleteAccount = async () => {
		setShowDeleteConfirmModal(false);
		await executeDeleteAccount(false);
	};

	const handleEmailChange = () => {
		if (!user) return;
		setEmailInputValue(user.email);
		setShowEmailChangeModal(true);
	};

	const confirmEmailChange = async () => {
		if (!emailInputValue || emailInputValue === user.email) {
			setShowEmailChangeModal(false);
			return;
		}

		setShowEmailChangeModal(false);
		await executeEmailChange(emailInputValue, false);
	};

	const closeReauthModal = () => {
		setShowReauthModal(false);
		setPendingAction(null);
		setReauthPassword("");
		setNewEmail("");
		setIsReauthLoading(false);
	};

	const handleReauthSubmit = async e => {
		e.preventDefault();
		if (!reauthPassword.trim()) {
			toast.error(translate("error.passwordRequired"));
			return;
		}

		setIsReauthLoading(true);

		try {
			if (pendingAction === "deleteAccount") {
				await executeDeleteAccount(true);
			} else if (pendingAction === "changeEmail") {
				await executeEmailChange(emailInputValue, true);
			}
		} catch (err) {
			toast.error(
				translate("error.reauthenticationFailed") + ": " + err.message
			);
		} finally {
			setIsReauthLoading(false);
		}
	};

	const effectiveTheme =
		themePreference === "system" ? systemTheme : themePreference;

	return (
		<div className="content">
			<StickyTitle title={translate("settings.title")} />

			{/* Re-authentication modal */}
			<Modal
				isOpen={showReauthModal}
				onClose={closeReauthModal}
				animated={true}
			>
				<ReauthForm
					reauthPassword={reauthPassword}
					setReauthPassword={setReauthPassword}
					onCancel={closeReauthModal}
					onSubmit={handleReauthSubmit}
					translate={translate}
					isLoading={isReauthLoading}
				/>
			</Modal>

			{/* Account deletion confirmation modal */}
			<Modal
				isOpen={showDeleteConfirmModal}
				onClose={() => setShowDeleteConfirmModal(false)}
				animated={true}
				modalType="delete"
			>
				<div className="reauth-modal-content glass-effect">
					<div className="settings-title-container">
						<AlertTriangle size={20} color="#ff4444" />
						<h2>{translate("settings.deleteAccount")}</h2>
					</div>
					<div className="layout-row design-form">
						<p>{translate("alert.confirmDeleteAccount")}</p>
					</div>
					<div className="ios-buttons">
						<button
							className="ios-button"
							onClick={() => setShowDeleteConfirmModal(false)}
						>
							<X size={18} />
							{translate("action.cancel")}
						</button>
						<button
							className="ios-button warning"
							onClick={confirmDeleteAccount}
						>
							<Trash2 size={18} />
							{translate("action.delete")}
						</button>
					</div>
				</div>
			</Modal>

			{/* Email change modal */}
			<Modal
				isOpen={showEmailChangeModal}
				onClose={() => setShowEmailChangeModal(false)}
				animated={true}
			>
				<div className="reauth-modal-content glass-effect">
					<div className="settings-title-container">
						<Pencil size={20} />
						<h2>{translate("settings.changeEmail")}</h2>
					</div>
					<div className="layout-row design-form">
						<label htmlFor="newEmail" className="new-email">
							{translate("settings.enterNewEmail")}
						</label>
						<input
							type="email"
							id="newEmail"
							className="field"
							onChange={e => setEmailInputValue(e.target.value)}
							placeholder={translate("login.emailPlaceholder")}
							autoFocus
						/>
					</div>
					<div className="ios-buttons">
						<button
							className="ios-button"
							onClick={() => setShowEmailChangeModal(false)}
						>
							<X size={18} />
							{translate("action.cancel")}
						</button>
						<button
							className="ios-button action"
							onClick={confirmEmailChange}
							disabled={!emailInputValue || emailInputValue === user?.email}
						>
							<Check size={18} />
							{translate("action.confirm")}
						</button>
					</div>
				</div>
			</Modal>

			<div className="page-content">
				<div className="card glass-effect settings-container">
					<div className="settings-title-container">
						<SunMoon size={20} />
						<h2>{translate("settings.theme")}</h2>
					</div>
					<div className="item-container">
						<label htmlFor="system-theme-toggle">
							{translate("settings.useSystemTheme")}
						</label>
						<input
							id="system-theme-toggle"
							type="checkbox"
							className="ios-switch"
							checked={themePreference === "system"}
							onChange={handleSystemThemeToggle}
						/>
					</div>
					<div className="item-container">
						<label htmlFor="manual-theme-toggle">
							{effectiveTheme === "dark"
								? translate("settings.dark")
								: translate("settings.light")}
						</label>
						<input
							id="manual-theme-toggle"
							type="checkbox"
							className="ios-switch"
							checked={effectiveTheme === "dark"}
							onChange={handleManualThemeToggle}
							disabled={themePreference === "system"}
						/>
					</div>
				</div>

				<div className="card glass-effect settings-container">
					<div className="settings-title-container">
						<Paintbrush size={20} />
						<h2>{translate("settings.design")}</h2>
					</div>
					<div className="item-container layout-row">
						<label
							htmlFor="nav-style-select"
							className="settings-text layout-col-2-3"
						>
							{translate("settings.bottomNavStyle")}
						</label>
						<div className="select-wrapper layout-col-1-3">
							<select
								id="nav-style-select"
								className="custom-select"
								value={bottomNavStyle}
								onChange={e => setBottomNavStyle(e.target.value)}
							>
								<option value="fixed">{translate("settings.fixed")}</option>
								<option value="floating">
									{translate("settings.floating")}
								</option>
								<option value="full">{translate("settings.full")}</option>
							</select>
						</div>
					</div>
					<div className="item-container layout-row">
						<label
							htmlFor="design-select"
							className="settings-text layout-col-2-3"
						>
							{translate("settings.designStyle")}
						</label>
						<div className="select-wrapper layout-col-1-3">
							<select
								id="design-select"
								className="custom-select"
								value={design}
								onChange={e => setDesign(e.target.value)}
							>
								<option value="ios18">
									{translate("settings.ios18Style")}
								</option>
								<option value="ios26">
									{translate("settings.ios26Style")}
								</option>
							</select>
						</div>
					</div>
				</div>

				<div className="card glass-effect settings-container">
					<div className="settings-title-container">
						<Languages size={20} />
						<h2>{translate("settings.language")}</h2>
					</div>
					<div className="item-container layout-row">
						<label
							htmlFor="language-select"
							className="settings-text layout-col-2-3"
						>
							{translate("settings.selectLanguage")}
						</label>
						<div className="select-wrapper layout-col-1-3">
							<select
								id="language-select"
								className="custom-select"
								value={selectedLanguage}
								onChange={handleLanguageChange}
							>
								<option value={LOCALES.ENGLISH}>English</option>
								<option value={LOCALES.FRENCH}>Français</option>
							</select>
						</div>
					</div>
				</div>

				{user && (
					<div className="card glass-effect settings-container">
						<div className="settings-title-container">
							<CircleUser size={20} />
							<h2>{translate("settings.account")}</h2>
						</div>
						<div className="item-container">
							<p>
								{translate("settings.email")} (
								{emailVerified
									? translate("settings.emailConfirmed")
									: translate("settings.emailNotConfirmed")}
								)
							</p>
							<p className="user-email">{user.email}</p>
						</div>
						{!emailVerified && (
							<div className="settings-container">
								<p className="error">{translate("alert.emailNotVerified")}</p>
								<button
									className="ios-button action"
									onClick={handleResendVerification}
								>
									<MailCheck size={18} />
									{translate("action.resendVerification")}
								</button>
							</div>
						)}
						{userData?.pendingEmail && (
							<div className="settings-container">
								<p className="error">
									{translate("settings.pendingEmailPrefix")}{" "}
									{userData.pendingEmail}
								</p>
								<p className="error">
									{translate("settings.pendingEmailInstructions")}
								</p>
							</div>
						)}
						<button className="ios-button action" onClick={handlePasswordReset}>
							<MailCheck size={18} />
							{translate("settings.resetPassword")}
						</button>
						<button className="ios-button action" onClick={handleEmailChange}>
							<Pencil size={18} />
							{translate("settings.changeEmail")}
						</button>
						<button
							className="ios-button action"
							onClick={handleExport}
							disabled={isExporting}
						>
							<FolderDown size={18} />
							{isExporting
								? translate("settings.exporting")
								: translate("settings.export")}
						</button>
						<button className="ios-button action" onClick={handleLogout}>
							<LogOut size={18} />
							{translate("settings.logout")}
						</button>
						<h3>{translate("settings.deleteAccount")}</h3>
						<button
							className="ios-button warning"
							onClick={handleDeleteAccount}
						>
							<Trash2 size={18} />
							{translate("settings.deleteAccountButton")}
						</button>
						<h3>{translate("settings.legalInfo")}</h3>
						<Link to="/terms" className="legal link-button">
							{translate("terms.title")}
						</Link>
						<Link to="/privacy-policy" className="legal link-button">
							{translate("privacy.title")}
						</Link>
					</div>
				)}

				{userData?.role === "admin" && (
					<div className="card glass-effect settings-container">
						<div className="settings-title-container">
							<ShieldUser size={18} />
							<h2>{translate("admin.title")}</h2>
						</div>
						<button
							className="ios-button action"
							onClick={handleNavigateToAdmin}
						>
							<ShieldUser size={18} />
							{translate("settings.accessAdmin")}
						</button>
					</div>
				)}

				<div className="build-id">
					<p>Build ID: {buildId}</p>
				</div>
			</div>
		</div>
	);
};

export default Settings;
